import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon } from '../common/EyeIcon';
import { InjuryBadge } from '../common/InjuryBadge';
import { Popover } from '../common/Popover';
import './today.css';
import type { GameCardModel, GoalRowModel, RosterEntry } from './gameViewModel';

const Name = ({ name }: { name: { text: string; tracked: boolean } }) =>
    name.tracked ? <span className="hl">{name.text}</span> : <>{name.text}</>;

function GoalRow({ goal }: { goal: GoalRowModel }) {
    const { t } = useTranslation();
    return (
        <li className="goal-row" data-testid="goal-row">
            <span className="goal-time">{goal.time}</span>
            <span className="goal-score">{goal.score}</span>
            <span className="goal-who">
                <Name name={goal.scorer} />
                {goal.detail && <span className="goal-detail"> {goal.detail}</span>}
                {goal.assists.length > 0 &&
                    <span className="goal-assists">
                        ({goal.assists.map((assist, index) => (
                            <span key={index}>{index > 0 && ', '}<Name name={assist} /></span>
                        ))})
                    </span>}
            </span>
            <span className="goal-video">
                {goal.videoUrl &&
                    <a href={goal.videoUrl} target="_blank" rel="noopener noreferrer" aria-label={`${t('goal_video')} ${goal.time}`}>▶</a>}
            </span>
        </li>
    );
}

function RosterName({ entry }: { entry?: RosterEntry }) {
    if (entry === undefined) {
        return <span />;
    }
    return (
        <span className={entry.dimmed ? 'roster-name dim' : 'roster-name hl'}>
            {entry.name}<InjuryBadge level={entry.injury} />
        </span>
    );
}

// The play button at a game's right edge and the menu of its videos.
function PlayMenu({ recapUrl, longUrl }: { recapUrl?: string; longUrl?: string }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const button = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => setOpen(false), []);

    return (
        <span className="popover-anchor">
            <button
                ref={button}
                type="button"
                className={`play-button${open ? ' open' : ''}`}
                aria-label={t('play_open')}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen(value => !value)}
            >
                ▶
            </button>
            <Popover open={open} onClose={close} anchorRef={button} label={t('play_open')} className="play-menu">
                {recapUrl && <a href={recapUrl} target="_blank" rel="noopener noreferrer" onClick={close}>{t('play_recap')}</a>}
                {longUrl && <a href={longUrl} target="_blank" rel="noopener noreferrer" onClick={close}>{t('play_condensed')}</a>}
            </Popover>
        </span>
    );
}

interface GameCardProps {
    card: GameCardModel;
    collapsed: boolean;
    onToggleCollapsed(): void;
    onReveal(): void;
}

// One game: a header row (tap it to collapse the card) and, below it, goals,
// goalies or the pregame roster.
export function GameCard({ card, collapsed, onToggleCollapsed, onReveal }: GameCardProps) {
    const { t } = useTranslation();
    const teams = (
        <span className="game-teams">
            <span data-slot="home">{card.home}</span> – <span data-slot="away">{card.away}</span>
        </span>
    );
    const headContent = (
        <>
            {teams}
            {card.score !== undefined && <span className="game-score" data-slot="score">{card.score}</span>}
            {card.ending !== undefined && <span className="game-ending" data-slot="ending">{card.ending}</span>}
            {card.clock !== undefined && <span className="game-clock" data-slot="clock">{card.clock}</span>}
        </>
    );

    const oneLine = card.mode !== 'full';
    const collapsible = card.hasBody;
    const showBody = card.hasBody && !collapsed;
    const hasVideos = card.recapUrl !== undefined || card.longUrl !== undefined;
    const matchup = `${card.home} – ${card.away}`;

    return (
        <article
            className={`game${oneLine ? ' game-quiet' : ''}`}
            data-testid="game-card"
            data-status={card.status.isLive ? 'live' : (card.status.isFinal ? 'final' : 'upcoming')}
            data-mode={card.mode}
        >
            <div className="game-head">
                {collapsible
                    ? <button type="button" className="game-main" aria-expanded={!collapsed} onClick={onToggleCollapsed}>{headContent}</button>
                    : <div className="game-main">{headContent}</div>}
                <span className="game-end">
                    {card.mode === 'hidden-team' && card.startLabel !== undefined &&
                        <span className="game-hidden" role="img" aria-label={t('hide_button')} data-testid="game-hidden"><EyeIcon /></span>}
                    {card.startLabel !== undefined && <span className="game-start" data-slot="start">{card.startLabel}</span>}
                    {oneLine && card.startLabel === undefined &&
                        <button type="button" className="reveal" aria-label={t('game_showFor', { teams: matchup })} onClick={onReveal}>
                            {t('game_show')}
                        </button>}
                    {hasVideos && <PlayMenu recapUrl={card.recapUrl} longUrl={card.longUrl} />}
                </span>
            </div>
            {card.meta !== undefined && <div className="game-meta">{card.meta}</div>}
            {showBody &&
                <div className="game-body">
                    {card.goals.length > 0 &&
                        <ol className="goal-list">{card.goals.map(goal => <GoalRow key={goal.key} goal={goal} />)}</ol>}
                    {card.goalies.length > 0 &&
                        <ul className="goalie-list">
                            {card.goalies.map(goalie => (
                                <li key={goalie.key} className={`goalie-row${goalie.tracked ? ' hl' : ''}`} data-testid="goalie-row">
                                    <span>{goalie.team}</span>
                                    <span>{goalie.name}</span>
                                    <span className="mono">{goalie.saves}</span>
                                    <span className="mono">{goalie.savePct}</span>
                                </li>
                            ))}
                        </ul>}
                    {card.roster !== undefined &&
                        <div className="roster" data-testid="roster">
                            {card.roster.map(row => (
                                <div key={row.key} className="roster-row">
                                    <RosterName entry={row.home} />
                                    <RosterName entry={row.away} />
                                </div>
                            ))}
                        </div>}
                </div>}
        </article>
    );
}
